import Head from 'next/head';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useAppSelector } from '@/redux';
import { useServices } from '@/services/useServices';

import LevelSection from '@/components/customerLoyalty/LevelSection';
import UserSection from '@/components/customerLoyalty/UserSection';
import EspotContainer from '@/components/layout/espots/EspotContainer';

import styles from './CustomerLoyaltyView.module.scss';
import { CUSTOMER_LOYALTY_COMPONENT_TYPE } from '@/constants/customerLoyaltyProgram';
import { ESPOT_TYPE } from '@/constants/espot';
import { FOOTER_TYPE } from '@/constants/footer';
import { PAGE_TYPE } from '@/constants/datalayer';

export const CustomerLoyaltyView = ({ customerLoyaltyProgram }) => {
  const currentLevel = useAppSelector(({ user }) => user.content?.currentLevel);
  const nextLevel = useAppSelector(({ user }) => user.content?.nextLevel);
  const { datalayerService } = useServices();

  useEffect(() => {
    datalayerService.pushPageName(PAGE_TYPE.LANDING_CUSTOMER_LOYALTY, PAGE_TYPE.LANDING_CUSTOMER_LOYALTY);
  }, []);

  const getLoyaltyProgramBlocs = (bloc, index) => {
    const { type } = bloc;
    const espotType = bloc?.espot?.type;

    let element;

    const className = [
      styles.bloc,
      espotType === ESPOT_TYPE.ESPOT_TRUSTED_SHOP && styles['no-padding']
    ].filter(Boolean).join(' ');

    switch (type) {
      case CUSTOMER_LOYALTY_COMPONENT_TYPE.ESPOT:
        element = <EspotContainer espot={bloc.espot} key={index} />;
        break;
      case CUSTOMER_LOYALTY_COMPONENT_TYPE.LEVEL:
        element = (
          <LevelSection
            title={bloc.levelSection.title}
            description={bloc.levelSection.description}
            levels={customerLoyaltyProgram.levels}
            userLevel={currentLevel}
            asterixContent={customerLoyaltyProgram.asterixContent}
            labelBtnCGU={customerLoyaltyProgram.labelBtnCGU}
            urlBtnCGU={customerLoyaltyProgram.urlBtnCGU}
          />
        );
        break;
      case CUSTOMER_LOYALTY_COMPONENT_TYPE.USER:
        element = <UserSection sectionData={bloc.userSection} currentLevel={currentLevel} nextLevel={nextLevel} />;
        break;
      default:
    }

    return (
      <div className={className}>
        {element}
      </div>
    );
  };

  return (
    <>
      <Head>
        <title>{customerLoyaltyProgram.metaTitle}</title>
        {customerLoyaltyProgram.metaDesc && <meta name="description" content={customerLoyaltyProgram.metaDesc}/>}
      </Head>

      <div className={styles.container}>
        <div
          className={styles['header-img']}
          style={{
            backgroundImage: `url(${customerLoyaltyProgram.layout.bgTitle})`
          }}
          data-test-id="header-img"
        >
          <span
            className={styles['header-title']}
            dangerouslySetInnerHTML={{ __html: customerLoyaltyProgram.layout.title }}
          />
        </div>
        {customerLoyaltyProgram.layout.sections.map((bloc, index) => getLoyaltyProgramBlocs(bloc, index))}
      </div>
    </>
  );
};

CustomerLoyaltyView.propTypes = {
  customerLoyaltyProgram: PropTypes.shape({
    metaTitle: PropTypes.string.isRequired,
    metaDesc: PropTypes.string,
    asterixContent: PropTypes.string,
    labelBtnCGU: PropTypes.string.isRequired,
    urlBtnCGU: PropTypes.string.isRequired,
    layout: PropTypes.shape({
      title: PropTypes.string.isRequired,
      bgTitle: PropTypes.string.isRequired,
      sections: PropTypes.array.isRequired
    }),
    levels: PropTypes.array.isRequired
  })
};

export default CustomerLoyaltyView;

/**
 * Prefetch function called on server side
 * @param {object} ctx The server side context
 * @param {object} store The server side application store
 * @param {object} props The seo page props
 * @return {object} Return an object containing the component props
 *    layoutOption : object containing the needed page layout (see _app component)
 *    namespaces : an array containing the needed translation namespaces
 */
export const prefetchView = async (ctx, { services }, props) => {
  const { storeId } = props;
  console.log('services :>> ', services.customerLoyaltyService);

  const customerLoyaltyProgram = await services.customerLoyaltyService.getCustomerLoyaltyByStoreId(storeId);

  return {
    customerLoyaltyProgram,
    layoutOptions: {
      footerType: FOOTER_TYPE.LANDING
    }
  };
};
